/**
 * Ödeme taahhüdü evrakında basılan üç tutarın TEK KAYNAKTAN türetilmesi.
 *
 * Sorun: "Genel Toplam" istemcide (KapakHesabi.ts) yeniden hesaplanıyordu, taksit
 * planının tabanı ise sunucuda (TaksitHesaplayici::getKapakHesabiGenelToplam)
 * hesaplanıyor. Aynı büyüklüğün iki bağımsız implementasyonu olduğu için sunucu
 * tarafındaki faiz/gecikme tazminatı optimizasyonundan sonra ikisi ayrıştı ve
 * evrakta "Genel Toplam + işleyecek faiz ≠ taahhüte esas borç" durumu oluştu.
 *
 * Çözüm: taksit planı varken üç tutar da AYNI taksit satırlarından türetilir:
 *
 *     taahhüte esas borç = Σ taksit.tutar
 *     işleyecek faiz     = Σ taksit.isleyen_faiz + Σ taksit.isleyen_faiz_bsmv
 *     Genel Toplam       = borç − işleyecek faiz
 *
 * BSMV'nin de düşülmesi ZORUNLUDUR. Planın tabanı sunucuda şöyle kurulur
 * (TaksitHesaplayici::hesaplaSayiyaGore, "nihai plan tabanı SATIRLARDAN türetilir"):
 *
 *     Σ taksit.tutar = kapak hesabı genel toplamı
 *                      + Σ isleyen_faiz + Σ isleyen_faiz_bsmv
 *
 * Yani plan süresince işleyen BSMV de taksitlerin içindedir. Yalnızca faiz
 * düşülünce BSMV "bugünkü borç" tarafında kalır ve Genel Toplam, evrakın kendi
 * kalem listesinden BSMV toplamı kadar fazla çıkar. (TDT-3234, 22.09 test dönüşü:
 * "Takipten sonra Bsmv olan takipler de Genel toplam ve Kalan rakam hatalı
 * görünmekte." Sahadaki evrak: Σtutar 5.668.272,12 − Σfaiz 2.384.358,54 =
 * 3.283.913,58 basılmıştı; kalemlerin gerçek toplamı 3.164.695,66 idi, aradaki
 * 119.217,92 tam olarak Σ isleyen_faiz_bsmv'dir.) BSMV'siz dosyalarda alan 0
 * geldiğinden davranış birebir aynı kalır.
 *
 * Kaynak olarak `taksit_hesap_ayar.toplam_borc` / `.isleyen_faiz` BİLEREK
 * kullanılmaz. O alanlar plan hesaplandığı anda dondurulur; taksit satırları
 * sonradan (ör. özel taksit ekleme, appendMode) yeniden hesaplatılmadan
 * değiştirilebildiği için bayat kalabilirler. Satırların kendisi ise evrakın
 * hemen altındaki taksit tablosunun da kaynağı olduğundan (GET
 * /api/v1/takip/{id}/taksit → takip->taksitler) her zaman günceldir; cümle ile
 * tablo yapısal olarak aynı veriden beslenir.
 */

/** `takip.taksit_hesap_ayar` — yalnızca "plan var mı" ve çapraz kontrol için. */
export interface TaahhutHesapAyar {
    ana_para?: number | string | null;
    isleyen_faiz?: number | string | null;
    /** TDT-3234: plan süresince işleyen BSMV (eski kayıtlarda bulunmaz). */
    isleyen_faiz_bsmv?: number | string | null;
    toplam_borc?: number | string | null;
}

/** GET /api/v1/takip/{id}/taksit yanıtındaki satırlardan evrak için gereken alanlar. */
export interface TaksitSatiri {
    tutar?: number | string | null;
    isleyen_faiz?: number | string | null;
    /** Plan süresince işleyen BSMV; taksit tutarının içindedir (BSMV'siz dosyada 0/null). */
    isleyen_faiz_bsmv?: number | string | null;
}

/** Taksit planı YOKKEN basılacak, istemcide hesaplanan tutarlar (eski davranış). */
export interface IstemciTutarlari {
    /** KapakHesabi genel toplamından gizlenen kalemler düşülmüş hâli (TL). */
    genelToplam: number;
    /** Eski evrak davranışı: netKalan + odemeTaahhuduToplamFaiz (TL). */
    taahhutBorcu: number;
}

/** Evrakta basılan üç tutar. Plan varken `genelToplam + isleyecekFaiz === taahhutBorcu`. */
export interface TaahhutTutarlari {
    /** "… Genel Toplam" satırı. */
    genelToplam: number;
    /** "TAAHHÜT TARİHİNDEN … İŞLEYECEK FAİZ … TL'DİR" cümlesi (BSMV dahildir). */
    isleyecekFaiz: number;
    /** `isleyecekFaiz` içindeki BSMV payı — evrakta "(BSMV DAHİL)" ibaresini açar. */
    isleyecekFaizBsmv: number;
    /** "… TAAHHÜTE ESAS BORCU … TL'DİR" cümlesi. */
    taahhutBorcu: number;
}

/** Kuruş hassasiyetine yuvarlar; kayan nokta artıklarını (0.1+0.2) temizler. */
function yuvarla(deger: number): number {
    return Math.round((deger + Number.EPSILON) * 100) / 100;
}

/** `"184892.30"` gibi string alanları da güvenle sayıya çevirir. */
function sayi(deger: number | string | null | undefined): number {
    if (deger === null || deger === undefined || deger === "") return 0;
    const n = Number(deger);
    return Number.isFinite(n) ? n : 0;
}

function topla(
    taksitler: TaksitSatiri[] | null | undefined,
    alan: keyof TaksitSatiri,
): number {
    if (!taksitler || taksitler.length === 0) return 0;
    return yuvarla(taksitler.reduce((toplam, t) => toplam + sayi(t ? t[alan] : 0), 0));
}

/**
 * Takibin kayıtlı bir taksit planı var mı?
 *
 * Evrakın eski davranışıyla aynı koşul: `takip.taksit_hesap_ayar` dolu ise plan vardır.
 */
export function taksitPlaniVarMi(hesapAyar: TaahhutHesapAyar | null | undefined): boolean {
    return !!hesapAyar;
}

/**
 * Evrakta basılacak üç tutarı üretir.
 *
 * Plan VARSA üçü de taksit satırlarından türetilir ve toplama garanti tutar.
 * Plan YOKSA istemci değerleri olduğu gibi basılır (eski davranış korunur);
 * o yolda toplama invaryantı iddia edilmez.
 *
 * @param hesapAyar   `takip.taksit_hesap_ayar` (yoksa null)
 * @param taksitler   Kayıtlı taksit satırları
 * @param istemci     Plan yokken kullanılacak istemci hesapları
 */
export function taahhutTutarlariHesapla(
    hesapAyar: TaahhutHesapAyar | null | undefined,
    taksitler: TaksitSatiri[] | null | undefined,
    istemci: IstemciTutarlari,
): TaahhutTutarlari {
    if (!taksitPlaniVarMi(hesapAyar)) {
        return {
            genelToplam: yuvarla(sayi(istemci.genelToplam)),
            isleyecekFaiz: 0,
            isleyecekFaizBsmv: 0,
            taahhutBorcu: yuvarla(sayi(istemci.taahhutBorcu)),
        };
    }

    const taahhutBorcu = topla(taksitler, "tutar");
    const planFaizi = topla(taksitler, "isleyen_faiz");
    const isleyecekFaizBsmv = topla(taksitler, "isleyen_faiz_bsmv");
    const isleyecekFaiz = yuvarla(planFaizi + isleyecekFaizBsmv);

    return {
        genelToplam: yuvarla(taahhutBorcu - isleyecekFaiz),
        isleyecekFaiz,
        isleyecekFaizBsmv,
        taahhutBorcu,
    };
}

/**
 * Evraktaki "Kalan" satırı: **Genel Toplam − Takip Sonrası Tahsilat**.
 *
 * Tahsilat yoksa Genel Toplam'ın kendisi basılır (çıkarma terimi 0'dır).
 *
 * Bu satır eskiden `KapakHesabi.netKalan`'dan besleniyordu; Genel Toplam ise plan
 * varken taksit satırlarından türetiliyor. İki bağımsız kaynak olduğu için ikisi
 * ayrışıyordu. Üstelik netKalan tahsilatsız dosyada NaN geliyor, `$helper.tutarStr`
 * içindeki `if (!val) val = 0` NaN'ı sessizce 0,00 TL'ye çeviriyordu — sahada
 * görülen "Kalan 0,00 TL" tam olarak buydu. `sayi()` sonlu olmayan her değeri
 * 0 saydığı için burada NaN bir daha sızamaz.
 */
export function kalanTutariHesapla(
    genelToplam: number | string | null | undefined,
    takipSonrasiTahsilat: number | string | null | undefined,
): number {
    return yuvarla(sayi(genelToplam) - sayi(takipSonrasiTahsilat));
}

/**
 * Evrak toplaması tutuyor mu? (`genelToplam + isleyecekFaiz === taahhutBorcu`)
 *
 * `taahhutTutarlariHesapla` çıktısı için her zaman true'dur; testlerde ve
 * regresyon kontrolünde invaryantı doğrulamak için kullanılır.
 */
export function tutarlarTutuyorMu(tutarlar: TaahhutTutarlari): boolean {
    return yuvarla(tutarlar.genelToplam + tutarlar.isleyecekFaiz) === tutarlar.taahhutBorcu;
}

/**
 * Teşhis: kaydedilmiş `taksit_hesap_ayar` özeti, canlı taksit satırlarıyla örtüşüyor mu?
 *
 * Evrak bu alanlara dayanmaz; false dönmesi "kayıtlı özet bayatlamış" demektir —
 * taksitlendirme akışında yeniden hesaplatmadan satır değiştirildiğinde olur.
 */
export function sunucuOzetiGuncelMi(
    hesapAyar: TaahhutHesapAyar | null | undefined,
    taksitler: TaksitSatiri[] | null | undefined,
): boolean {
    if (!hesapAyar) return true;
    return (
        yuvarla(sayi(hesapAyar.toplam_borc)) === topla(taksitler, "tutar") &&
        yuvarla(sayi(hesapAyar.isleyen_faiz)) === topla(taksitler, "isleyen_faiz")
    );
}

/** Taksit ekranlarının özet şeridi ("Borç Tutarı / Faiz Tutarı / Toplam Borç"). */
export interface PlanOzeti {
    /** Kapak genel toplamı — plan süresince işleyen faiz ve BSMV HARİÇ. */
    borcTutari: number;
    /** Plan süresince işleyen faiz + BSMV (evrakın "işleyecek faiz" cümlesiyle aynı). */
    faizTutari: number;
    /** faizTutari içindeki BSMV payı ("BSMV dahil" ibaresi için). */
    faizBsmv: number;
    /** Σ taksit tutarı (evrakın "taahhüte esas borç" cümlesiyle aynı). */
    toplamBorc: number;
}

/**
 * `takip.taksit_hesap_ayar` kayıtlı planda JSON STRING, hesaplama yanıtında nesne olarak gelir.
 * Bozuk/boş değerde null döner (ekran "plan yok" gibi davranır, patlamaz).
 */
export function hesapAyarCoz(girdi: any): TaahhutHesapAyar | null {
    if (girdi === null || girdi === undefined || girdi === "") return null;
    if (typeof girdi === "string") {
        try {
            const nesne = JSON.parse(girdi);
            return nesne && typeof nesne === "object" ? nesne : null;
        } catch (e) {
            return null;
        }
    }
    return typeof girdi === "object" ? girdi : null;
}

/**
 * TDT-3234: taksit ekranlarının özet şeridi evrakla AYNI kaynaktan türetilir.
 *
 * Eskiden şeritler `taksit_hesap_ayar.ana_para` / `.isleyen_faiz` gösteriyordu. `ana_para`
 * (Σtutar − Σfaiz) plan süresince işleyen BSMV'yi de içerdiği için BSMV'li dosyada "Borç
 * Tutarı" evrakın Genel Toplam'ından BSMV kadar fazla, "Faiz Tutarı" ise eksik çıkıyordu.
 *
 * Satırlar varsa kaynak satırlardır (taahhutTutarlariHesapla — evrakla birebir). Satır yoksa
 * kayıtlı özet kullanılır: yeni kayıtlarda `ana_para` BSMV hariçtir ve `isleyen_faiz_bsmv`
 * ayrıca gelir; eski kayıtlarda alan yoktur (0 sayılır) ve toplam eskisi gibi kalır.
 */
export function planOzetiHesapla(
    hesapAyarGirdi: any,
    taksitler: TaksitSatiri[] | null | undefined,
): PlanOzeti {
    const hesapAyar = hesapAyarCoz(hesapAyarGirdi);
    if (!hesapAyar) {
        return {borcTutari: 0, faizTutari: 0, faizBsmv: 0, toplamBorc: 0};
    }

    if (taksitler && taksitler.length > 0) {
        const t = taahhutTutarlariHesapla(hesapAyar, taksitler, {genelToplam: 0, taahhutBorcu: 0});
        return {
            borcTutari: t.genelToplam,
            faizTutari: t.isleyecekFaiz,
            faizBsmv: t.isleyecekFaizBsmv,
            toplamBorc: t.taahhutBorcu,
        };
    }

    const ana = sayi(hesapAyar.ana_para);
    const faiz = sayi(hesapAyar.isleyen_faiz);
    const bsmv = sayi(hesapAyar.isleyen_faiz_bsmv);
    return {
        borcTutari: yuvarla(ana),
        faizTutari: yuvarla(faiz + bsmv),
        faizBsmv: yuvarla(bsmv),
        toplamBorc: yuvarla(ana + faiz + bsmv),
    };
}

/**
 * Kayıtlı plan listesinin (TaksitTaahutList) özeti. Satırlar "Tahsilata Dönüştür" ile
 * değişebilir (tutar düşer, tamamen ödenen satır silinir); o durumda satırlardan türetmek
 * planı değil kalanı gösterir (kısmi ödemede "Borç Tutarı" eksiye bile düşer). Bu yüzden
 * satırlar KAYITLI özetle hâlâ uyuşuyorsa satırlar (evrakla aynı hesap), uyuşmuyorsa
 * kaydedilen plan özeti kullanılır.
 */
export function kayitliPlanOzetiHesapla(
    hesapAyarGirdi: any,
    taksitler: TaksitSatiri[] | null | undefined,
): PlanOzeti {
    const hesapAyar = hesapAyarCoz(hesapAyarGirdi);
    const satirlarKayitliPlanla = !!hesapAyar && !!taksitler && taksitler.length > 0
        && sunucuOzetiGuncelMi(hesapAyar, taksitler);
    return planOzetiHesapla(hesapAyar, satirlarKayitliPlanla ? taksitler : []);
}

/**
 * TDT-3234 (28.09): plan varken evrakta basılan "Genel Toplam".
 *
 * Planın tabanı kapak hesabının NET genel toplamıdır (tahsil edilenler düşülmüş). Evrakın
 * kalem listesi ise takip sonrası kalemleri brüt basar ve altına "Takip Sonrası Tahsilat"
 * ile "Kalan = Genel Toplam − Tahsilat" gelir. Net tabanı Genel Toplam diye basmak
 * tahsilatlı dosyada tahsilatı İKİ KEZ düşürüyordu. Genel Toplam = net taban + takip
 * sonrası tahsilat; böylece Kalan tam olarak net tabana (planın ödettiği borca) eşit olur.
 * Tahsilatsız dosyada davranış değişmez.
 */
export function planliGenelToplamHesapla(
    planTabani: number | string | null | undefined,
    takipSonrasiTahsilat: number | string | null | undefined,
): number {
    return yuvarla(sayi(planTabani) + sayi(takipSonrasiTahsilat));
}
